/**
 * LiveKit SFU pod readiness for session list actions.
 *
 * green  — pod ready (livekit_infra_ready + per-session url)
 * yellow — provisioning / starting
 * red    — no pod yet (LiveKit session without url/ready)
 * null   — not a LiveKit session (hide dot)
 */

import { Tooltip, Box, keyframes } from "@mui/material";
import type { Session } from "./sessionTypes";

export type LiveKitPodDotStatus = "ready" | "starting" | "none";

function asBool(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return false;
}

function sessionField(session: Session, snake: string, camel: string): unknown {
  const row = session as Session & Record<string, unknown>;
  return row[snake] ?? row[camel];
}

/** Shared apex SFU must not count as a ready per-session pod. */
export function isSharedLiveKitUrl(url: string): boolean {
  const host = String(url || "")
    .trim()
    .toLowerCase()
    .replace(/^wss?:\/\//, "")
    .split("/")[0]
    .split(":")[0];
  return (
    host === "livekit.exskilence.com" ||
    host === "www.livekit.exskilence.com"
  );
}

export function resolveLiveKitPodStatus(
  session: Session,
): LiveKitPodDotStatus | null {
  const provider = String(
    sessionField(session, "session_provider", "sessionProvider") || "",
  )
    .trim()
    .toLowerCase();
  if (provider !== "livekit") return null;

  const rawUrl = String(
    sessionField(session, "livekit_url", "livekitUrl") || "",
  ).trim();
  const url = rawUrl && !isSharedLiveKitUrl(rawUrl) ? rawUrl : "";
  const ready = asBool(
    sessionField(session, "livekit_infra_ready", "livekitInfraReady"),
  );
  const scale = String(
    sessionField(
      session,
      "livekit_infra_scale_status",
      "livekitInfraScaleStatus",
    ) || "",
  )
    .trim()
    .toLowerCase();

  if (ready && url) return "ready";
  // Provisioning / warm-up: URL present, scaled_up, or any non-empty scale status
  // other than scaled_down / idle.
  if (
    url ||
    scale === "scaled_up" ||
    scale === "scaling" ||
    scale === "provisioning" ||
    scale === "starting"
  ) {
    return "starting";
  }
  return "none";
}

/** True when a LiveKit row still needs pod readiness polling. */
export function needsLiveKitPodStatusPoll(session: Session): boolean {
  const status = resolveLiveKitPodStatus(session);
  return status === "none" || status === "starting";
}

const DOT_META: Record<
  LiveKitPodDotStatus,
  { color: string; label: string }
> = {
  ready: { color: "#16a34a", label: "Media pod ready" },
  starting: { color: "#eab308", label: "Media pod starting…" },
  none: { color: "#dc2626", label: "No media pod yet" },
};

const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
`;

export function LiveKitPodStatusDot({ session }: { session: Session }) {
  const status = resolveLiveKitPodStatus(session);
  if (!status) return null;
  const meta = DOT_META[status];
  return (
    <Tooltip title={meta.label} arrow>
      <Box
        component="span"
        aria-label={meta.label}
        sx={{
          display: "inline-block",
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: meta.color,
          boxShadow: `0 0 0 1.5px ${meta.color}33`,
          flexShrink: 0,
          ...(status === "starting"
            ? { animation: `${pulse} 1.2s ease-in-out infinite` }
            : null),
        }}
      />
    </Tooltip>
  );
}
